import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";

/**
 * Global registry of long-running AI tasks so we can render a
 * persistent "AI is bezig"-pillbadge from `AppLayout`, regardless
 * of which page the user is currently on.
 *
 * Contract:
 *  - `startAITask({ id, label, returnTo })` when the user kicks off
 *    a background AI generation. `id` must be unique enough to prevent
 *    duplicates on re-registers (e.g. "vacatures-<coacheeId>").
 *    Re-registering the same id is idempotent — the second call
 *    just refreshes `startedAt` / `label` / `returnTo`.
 *  - `endAITask(id)` when the task is done or failed.
 *  - `tasks` is an array of `{ id, label, returnTo, startedAt }`.
 *
 * Storage is intentionally in-memory. If the user reloads the tab
 * mid-generation, they lose the badge — but they will still see the
 * "Even geduld"-scherm on the originating page thanks to the poll
 * pattern the routes/*.py background-task pattern gives us.
 */

const AITaskContext = createContext(null);

export function AITaskProvider({ children }) {
  const [tasks, setTasks] = useState([]);

  const startAITask = useCallback((task) => {
    if (!task || !task.id) return;
    setTasks((prev) => {
      const idx = prev.findIndex((t) => t.id === task.id);
      const entry = {
        id: task.id,
        label: task.label || "AI-taak",
        returnTo: task.returnTo || null,
        startedAt: task.startedAt || Date.now(),
      };
      if (idx >= 0) {
        const copy = prev.slice();
        copy[idx] = { ...prev[idx], ...entry };
        return copy;
      }
      return [...prev, entry];
    });
  }, []);

  const endAITask = useCallback((id) => {
    if (!id) return;
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(
    () => ({ tasks, startAITask, endAITask }),
    [tasks, startAITask, endAITask],
  );
  return <AITaskContext.Provider value={value}>{children}</AITaskContext.Provider>;
}

export function useAITasks() {
  const ctx = useContext(AITaskContext);
  if (!ctx) throw new Error("useAITasks must be used inside <AITaskProvider>");
  return ctx;
}

/**
 * Convenience hook: registers a task while a component-scoped flag
 * (`active`) is true, and automatically unregisters on unmount.
 * Use this in pages where you already have an `isGenerating` boolean.
 */
export function useAITaskRegistration({ active, id, label, returnTo }) {
  const { startAITask, endAITask } = useAITasks();
  useEffect(() => {
    if (!active || !id) return undefined;
    startAITask({ id, label, returnTo, startedAt: Date.now() });
    return () => endAITask(id);
  }, [active, id, label, returnTo, startAITask, endAITask]);
}
